import { Fragment, useEffect, useRef, useState } from "react";
import "./Files.scss";
import axios from "axios";
import { Dialog, Menu, Transition } from "@headlessui/react";
import {
  Check,
  ChevronDown,
  Copy,
  Download,
  FileText,
  Filter,
  FolderInput,
  Grid3X3,
  List,
  MoreHorizontal,
  Pencil,
  Search,
  Share2,
  Star,
  StickyNote,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import TagsInput from "react-tagsinput";
import "react-tagsinput/react-tagsinput.css";
import { ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { toastify } from "../../components/Toast";
import MoveModal from "../../shades/cards/includes/MoveModal";
import ShareCard from "../../shades/cards/shareCard";

const sortOptions = [
  { label: "Newest", value: "Created_Date" },
  { label: "Recent activity", value: "Recent_Activity" },
  { label: "Name A–Z", value: "Ascending_Order" },
  { label: "Name Z–A", value: "Descending_Order" },
];

const getFileExtension = (name = "") => {
  const extension = name.split(".").pop();
  return extension && extension !== name ? extension.toLowerCase() : "file";
};

const getFileUrl = (file) => file?.content?.imageUrl?.[0]?.url || "";

const getFilePresentation = (extension) => {
  const fileType = extension.toLowerCase();

  if (fileType === "pdf") return { tone: "pdf", label: "PDF" };
  if (["doc", "docx", "rtf"].includes(fileType)) {
    return { tone: "document", label: fileType.toUpperCase() };
  }
  if (["xls", "xlsx", "csv"].includes(fileType)) {
    return { tone: "sheet", label: fileType.toUpperCase() };
  }
  if (["ppt", "pptx", "key"].includes(fileType)) {
    return { tone: "presentation", label: fileType.toUpperCase() };
  }
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif"].includes(fileType)) {
    return { tone: "image", label: fileType.toUpperCase() };
  }
  if (["mp4", "mov", "avi", "webm", "mkv"].includes(fileType)) {
    return { tone: "video", label: fileType.toUpperCase() };
  }
  if (["mp3", "wav", "aac", "m4a", "ogg"].includes(fileType)) {
    return { tone: "audio", label: fileType.toUpperCase() };
  }
  if (["zip", "rar", "7z", "tar", "gz"].includes(fileType)) {
    return { tone: "archive", label: fileType.toUpperCase() };
  }
  if (["js", "jsx", "ts", "tsx", "html", "css", "json", "py", "php"].includes(fileType)) {
    return { tone: "code", label: fileType.toUpperCase() };
  }

  return { tone: "generic", label: fileType.toUpperCase().slice(0, 5) || "FILE" };
};

const FileTypeIcon = ({ name, size = "medium" }) => {
  const { tone, label } = getFilePresentation(getFileExtension(name));

  return (
    <span className={`files-type-icon files-type-icon--${tone} files-type-icon--${size}`} aria-label={`${label} file`}>
      <svg viewBox="0 0 40 52" fill="none" aria-hidden="true">
        <path className="files-type-icon__paper" d="M4 4C4 1.79086 5.79086 0 8 0H26L36 10V48C36 50.2091 34.2091 52 32 52H8C5.79086 52 4 50.2091 4 48V4Z" stroke="currentColor" strokeWidth="2" />
        <path d="M26 0V6C26 8.20914 27.7909 10 30 10H36" stroke="currentColor" strokeWidth="2" />
        <line x1="10" y1="22" x2="30" y2="22" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" />
        <line x1="10" y1="28" x2="26" y2="28" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" />
        <line x1="10" y1="34" x2="28" y2="34" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" />
      </svg>
      <span>{label}</span>
    </span>
  );
};

const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif"];
const videoExtensions = ["mp4", "mov", "avi", "webm", "mkv"];

const FilePreview = ({ file, size = "medium" }) => {
  const extension = getFileExtension(file?.title);
  const fileUrl = getFileUrl(file);
  const isImage = imageExtensions.includes(extension);
  const isVideo = videoExtensions.includes(extension);

  if (fileUrl && isImage) {
    return <img className={`files-media-preview files-media-preview--${size}`} src={fileUrl} alt={file?.title || "File preview"} />;
  }

  if (fileUrl && isVideo) {
    return (
      <span className={`files-media-preview files-media-preview--${size} files-media-preview--video`}>
        <video src={fileUrl} muted playsInline preload="metadata" aria-label={`${file?.title || "File"} video preview`} />
        <span className="files-media-preview__play" aria-hidden="true" />
      </span>
    );
  }

  return <FileTypeIcon name={file?.title} size={size} />;
};

const formatFileSize = (bytes) => {
  if (typeof bytes !== "number" || Number.isNaN(bytes) || bytes < 0) return "—";
  if (bytes === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** index;
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${units[index]}`;
};

const formatDate = (date) => {
  if (!date) return "—";
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "—";

  return parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getTagLabel = (file) => {
  const firstTag = Array.isArray(file?.tags) ? file.tags[0] : null;
  if (!firstTag) return "No tags";
  return typeof firstTag === "string" ? firstTag : firstTag?.name || "No tags";
};

const getSharedLabel = (file) => {
  const names = (file?.shared || [])
    .map((share) => share?.sharedWith?.fullName)
    .filter(Boolean);

  return names.length ? names.join(", ") : "Private";
};

const getNotePreview = (file) => String(file?.note || "")
  .replace(/<[^>]+>/g, " ")
  .replace(/\s+/g, " ")
  .trim();

const getErrorMessage = (error, fallback) => error?.response?.data?.message || fallback;

function Files() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const initialSort = localStorage.getItem("cardFilter") || "Created_Date";

  const [filesData, setFilesData] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fileRefresh, setFileRefresh] = useState(false);
  const [cardFilterDefault, setCardFilterDefault] = useState(initialSort);
  const [viewMode, setViewMode] = useState("list");
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const [filePopup, setFilePopup] = useState(false);
  const [files, setFiles] = useState([]);
  const [note, setNotes] = useState("");
  const [tags, setTags] = useState([]);
  const [inputValueSet, setInputValueSet] = useState("");
  const [selectedBucket, setSelectedBucket] = useState("");
  const [searchBucket, setSearchBucket] = useState("");
  const [isBucketOpen, setIsBucketOpen] = useState(false);
  const [isTagOpen, setIsTagOpen] = useState(false);

  const [filterSelectedBucket, setFilterSelectedBucket] = useState("");
  const [filterBucket, setFilterBucket] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [filterTagId, setFilterTagId] = useState("");

  const [fileSharePopup, setFileSharePopup] = useState(false);
  const [shareFileId, setShareFileId] = useState("");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingFileId, setEditingFileId] = useState("");
  const [editingFileName, setEditingFileName] = useState("");
  const [isCopyOpen, setIsCopyOpen] = useState(false);
  const [copyFileId, setCopyFileId] = useState("");
  const [copyBucketId, setCopyBucketId] = useState("");
  const [copyBucketSearch, setCopyBucketSearch] = useState("");
  const [moveModal, setMoveModal] = useState(false);
  const [openMove, setOpenMove] = useState(false);

  const activeSort = sortOptions.find((option) => option.value === cardFilterDefault) || sortOptions[0];
  const filteredBuckets = buckets.filter((bucket) => {
    const query = searchBucket.trim().toLowerCase();
    if (!query) return true;

    return (
      bucket?.name?.toLowerCase().includes(query) ||
      bucket?.subBuckets?.some((subBucket) => subBucket?.name?.toLowerCase().includes(query))
    );
  });
  const filteredTags = tagOptions.filter((tag) =>
    tag?.name?.toLowerCase().includes(inputValueSet.trim().toLowerCase())
  );
  const visibleFiles = filesData.filter((file) =>
    (file?.title || "").toLowerCase().includes(searchQuery.trim().toLowerCase())
  );
  const visibleFileIds = visibleFiles.map((file) => file?._id).filter(Boolean);
  const areAllVisibleFilesSelected =
    visibleFileIds.length > 0 && visibleFileIds.every((fileId) => selectedItems.includes(fileId));
  const hasFilters = Boolean(filterSelectedBucket || filterTagId);
  const copyBuckets = buckets.filter((bucket) => {
    const query = copyBucketSearch.trim().toLowerCase();
    if (!query) return true;
    return (
      bucket?.name?.toLowerCase().includes(query) ||
      bucket?.subBuckets?.some((subBucket) => subBucket?.name?.toLowerCase().includes(query))
    );
  });

  const refreshFiles = () => setFileRefresh((previous) => !previous);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsLoading(false);
      return undefined;
    }

    const fetchFilters = async () => {
      try {
        const [bucketResponse, tagResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API}buckets/user-buckets`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API}tags/list`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setBuckets(bucketResponse?.data?.data?.bucketList || []);
        setTagOptions(tagResponse?.data?.data || []);
      } catch (error) {
        console.error("Unable to load file filters:", error);
      }
    };

    fetchFilters();
    return undefined;
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsLoading(false);
      return undefined;
    }

    const fetchFiles = async () => {
      setIsLoading(true);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API}cards/cards-with-files`,
          {
            filterValue: cardFilterDefault,
            limit: 100,
            bucketId: filterSelectedBucket,
            tagId: filterTagId,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFilesData(response?.data?.data || []);
      } catch (error) {
        console.error("Unable to load files:", error);
        toastify("error", getErrorMessage(error, "Unable to load files."));
      } finally {
        setIsLoading(false);
      }
    };

    fetchFiles();
    return undefined;
  }, [cardFilterDefault, filterSelectedBucket, filterTagId, fileRefresh]);

  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    setSelectedItems((previous) => {
      if (checked) return previous.includes(value) ? previous : [...previous, value];
      return previous.filter((item) => item !== value);
    });
  };

  const toggleVisibleSelection = () => {
    setSelectedItems((previous) => {
      if (areAllVisibleFilesSelected) {
        return previous.filter((fileId) => !visibleFileIds.includes(fileId));
      }
      return [...new Set([...previous, ...visibleFileIds])];
    });
  };

  const updateCard = async (cardId, starred) => {
    const token = localStorage.getItem("token");
    setFilesData((previous) => previous.map((file) => (
      file?._id === cardId ? { ...file, starred } : file
    )));

    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        { cardId, starred },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response?.data?.message || "File updated.");
    } catch (error) {
      setFilesData((previous) => previous.map((file) => (
        file?._id === cardId ? { ...file, starred: !starred } : file
      )));
      console.error("Unable to update file:", error);
      toastify("error", getErrorMessage(error, "Unable to update file."));
    }
  };

  const handleStarClick = (file) => updateCard(file?._id, !file?.starred);

  const validateNewFiles = (newFiles) => {
    const currentFiles = files.length;
    const totalSize = newFiles.reduce((total, file) => total + file.size, 0);

    if (currentFiles + newFiles.length > 3) {
      toastify("error", "Please select up to 3 files.");
      return false;
    }
    if (newFiles.length === 1 && newFiles[0].size > 10 * 1024 * 1024) {
      toastify("error", "File size should be less than 10 MB.");
      return false;
    }
    if (newFiles.length > 1 && totalSize > 30 * 1024 * 1024) {
      toastify("error", "Selected files should be less than 30 MB.");
      return false;
    }

    return true;
  };

  const addFiles = (newFiles) => {
    if (!newFiles.length || !validateNewFiles(newFiles)) return;
    setFiles((previous) => [...previous, ...newFiles]);
  };

  const handleFileChange = (event) => {
    event.preventDefault();
    addFiles(Array.from(event.target.files || []));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    addFiles(Array.from(event.dataTransfer.files || []));
  };

  const handleRemoveFile = (indexToRemove) => {
    setFiles((previous) => previous.filter((_, index) => index !== indexToRemove));
  };

  const closeFilePopup = () => {
    setFilePopup(false);
    setFiles([]);
    setTags([]);
    setNotes("");
    setSelectedBucket("");
    setSearchBucket("");
    setInputValueSet("");
    setIsBucketOpen(false);
    setIsTagOpen(false);
  };

  const handleSubmit = async () => {
    const userData = JSON.parse(localStorage.getItem("userData") || "null");
    const token = localStorage.getItem("token");

    if (!files.length) {
      toastify("error", "Choose at least one file to upload.");
      return;
    }
    if (!userData?._id) {
      toastify("error", "Your session has expired. Please sign in again.");
      return;
    }

    const formData = new FormData();
    formData.append("tags", JSON.stringify(tags.map((tag) => ({ name: tag }))));
    formData.append("note", note || "");
    formData.append("userId", userData._id);
    formData.append("bucketId", selectedBucket);
    files.forEach((file) => formData.append("myFiles", file));

    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API}cards/upload-files`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toastify("success", response?.data?.message || "Files uploaded.");
      closeFilePopup();
      refreshFiles();
    } catch (error) {
      console.error("Unable to upload files:", error);
      toastify("error", getErrorMessage(error, "Unable to upload files."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadFile = async (file) => {
    const fileUrl = getFileUrl(file);
    if (!fileUrl) {
      toastify("error", "This file does not have a downloadable URL.");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API}cards/download-file`,
        { fileUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const base64Data = response?.data?.data?.fileBase64;
      if (!base64Data) throw new Error("No download data received");

      const bytes = Uint8Array.from(atob(base64Data), (character) => character.charCodeAt(0));
      const blob = new Blob([bytes], { type: getFileExtension(file?.title) });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = file?.title || `file.${getFileExtension(file?.title)}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Unable to download file:", error);
      toastify("error", getErrorMessage(error, "Unable to download file."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectedDownload = () => {
    filesData
      .filter((file) => selectedItems.includes(file?._id))
      .forEach((file) => handleDownloadFile(file));
  };

  const handleDeleteCard = async () => {
    if (!selectedItems.length) return;

    const token = localStorage.getItem("token");
    setIsLoading(true);
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API}cards/delete`, {
        data: { deletedIds: selectedItems },
        headers: { Authorization: `Bearer ${token}` },
      });
      toastify("success", response?.data?.message || "Files deleted.");
      setSelectedItems([]);
      setIsDeleteOpen(false);
      refreshFiles();
    } catch (error) {
      console.error("Unable to delete files:", error);
      toastify("error", getErrorMessage(error, "Unable to delete files."));
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteFor = (fileId) => {
    setSelectedItems([fileId]);
    setIsDeleteOpen(true);
  };

  const openMoveFor = (fileId) => {
    setSelectedItems([fileId]);
    setMoveModal(true);
    setOpenMove(true);
  };

  const openEditFor = (file) => {
    setEditingFileId(file?._id);
    setEditingFileName(file?.title || "");
    setIsEditOpen(true);
  };

  const handleRenameFile = async () => {
    const nextName = editingFileName.trim();
    if (!nextName) {
      toastify("error", "File name cannot be empty.");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        { cardId: editingFileId, title: nextName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response?.data?.message || "File renamed.");
      setIsEditOpen(false);
      refreshFiles();
    } catch (error) {
      console.error("Unable to rename file:", error);
      toastify("error", getErrorMessage(error, "Unable to rename file."));
    }
  };

  const openCopyFor = (fileId) => {
    setCopyFileId(fileId);
    setCopyBucketId("");
    setCopyBucketSearch("");
    setIsCopyOpen(true);
  };

  const selectCopyBucket = (bucket) => {
    setCopyBucketId(bucket?._id);
    setCopyBucketSearch(bucket?.name || "");
  };

  const handleCopyFile = async () => {
    if (!copyBucketId) {
      toastify("error", "Select a destination bucket first.");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        { optionType: "CopyTo", destinationBucketID: copyBucketId, cardId: copyFileId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response?.data?.message || "File copied.");
      setIsCopyOpen(false);
      refreshFiles();
    } catch (error) {
      console.error("Unable to copy file:", error);
      toastify("error", getErrorMessage(error, "Unable to copy file."));
    }
  };

  const openMoveForSelected = () => {
    if (!selectedItems.length) return;
    setMoveModal(true);
    setOpenMove(true);
  };

  const closeMove = () => {
    setSelectedItems([]);
    setMoveModal(false);
    setOpenMove(false);
  };

  const handleSharePop = (fileId) => {
    setShareFileId(fileId);
    setFileSharePopup(true);
  };

  const handleFilterFilesText = (option) => {
    localStorage.setItem("cardFilter", option.value);
    setCardFilterDefault(option.value);
  };

  const clearFilters = () => {
    setFilterSelectedBucket("");
    setFilterBucket("");
    setFilterTagId("");
    setFilterTag("");
  };

  const selectFilterBucket = (bucket) => {
    setFilterSelectedBucket(bucket._id);
    setFilterBucket(bucket.name);
    setFilterTagId("");
    setFilterTag("");
    setShowFilters(false);
  };

  const selectFilterTag = (tag) => {
    setFilterTagId(tag._id);
    setFilterTag(tag.name);
    setFilterSelectedBucket("");
    setFilterBucket("");
    setShowFilters(false);
  };

  const selectUploadBucket = (bucket) => {
    setSelectedBucket(bucket._id);
    setSearchBucket(bucket.name);
    setIsBucketOpen(false);
  };

  const selectUploadSubBucket = (bucket) => {
    setSelectedBucket(bucket._id);
    setSearchBucket(bucket.name);
    setIsBucketOpen(false);
  };

  const selectUploadTag = (tag) => {
    if (!tags.includes(tag.name)) setTags((previous) => [...previous, tag.name]);
    setInputValueSet("");
    setIsTagOpen(false);
  };

  const renderFileActions = (file) => (
    <Menu as="div" className="files-action-menu" onClick={(event) => event.stopPropagation()}>
      <Menu.Button className="files-icon-button files-icon-button--quiet" aria-label={`Actions for ${file?.title || "file"}`}>
        <MoreHorizontal size={18} aria-hidden="true" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="files-action-menu__items">
          <Menu.Item>
            {({ active }) => (
              <button className={active ? "is-active" : ""} onClick={() => openEditFor(file)}>
                <Pencil size={16} /> Edit name
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button className={active ? "is-active" : ""} onClick={() => handleDownloadFile(file)}>
                <Download size={16} /> Download
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button className={active ? "is-active" : ""} onClick={() => handleSharePop(file?._id)}>
                <Share2 size={16} /> Share
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button className={active ? "is-active" : ""} onClick={() => openMoveFor(file?._id)}>
                <FolderInput size={16} /> Move to
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button className={active ? "is-active" : ""} onClick={() => openCopyFor(file?._id)}>
                <Copy size={16} /> Copy to
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button className={`files-action-menu__danger ${active ? "is-active" : ""}`} onClick={() => openDeleteFor(file?._id)}>
                <Trash2 size={16} /> Delete
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );

  const renderSelectionControl = (file) => {
    const selected = selectedItems.includes(file?._id);

    return (
      <label className={`files-checkbox ${selected ? "is-selected" : ""}`} onClick={(event) => event.stopPropagation()}>
        <input
          type="checkbox"
          value={file?._id}
          checked={selected}
          onChange={handleCheckboxChange}
          aria-label={`Select ${file?.title || "file"}`}
        />
        <span>{selected && <Check size={12} strokeWidth={3} />}</span>
      </label>
    );
  };

  return (
    <>
      {isLoading ? (
        <div className="App-loading">
          <div className="App-logo" title="Zimzee" />
        </div>
      ) : (
        <section className="files-page">
          <ToastContainer />

          <header className="files-page__header">
            <div>
              <div className="files-page__title-row">
                <h1>Files</h1>
                <span className="files-page__count">{filesData.length}</span>
              </div>
              <p>Organize, share, and access your uploaded files.</p>
            </div>
            <button type="button" className="files-button files-button--primary" onClick={() => setFilePopup(true)}>
              <Upload size={17} />
              <span>Upload file</span>
            </button>
          </header>

          <div className="files-toolbar">
            <div className="files-toolbar__search">
              <Search size={18} aria-hidden="true" />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search files..."
                aria-label="Search files"
              />
            </div>

            <div className="files-toolbar__controls">
              <div className="files-filter-control">
                <button
                  type="button"
                  className={`files-button files-button--outline ${hasFilters ? "files-button--filtered" : ""}`}
                  onClick={() => setShowFilters((previous) => !previous)}
                  aria-expanded={showFilters}
                >
                  <Filter size={16} />
                  <span>Filter</span>
                  <ChevronDown size={15} />
                </button>

                {showFilters && (
                  <div className="files-filter-popover">
                    <div className="files-filter-popover__header">
                      <span>Filter files</span>
                      {hasFilters && (
                        <button type="button" onClick={clearFilters}>
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="files-filter-popover__section">
                      <span>Bucket</span>
                      <button
                        type="button"
                        className={!filterSelectedBucket ? "is-selected" : ""}
                        onClick={() => {
                          setFilterSelectedBucket("");
                          setFilterBucket("");
                          setShowFilters(false);
                        }}
                      >
                        All buckets
                      </button>
                      {buckets.map((bucket) => (
                        <Fragment key={bucket?._id}>
                          <button
                            type="button"
                            className={filterSelectedBucket === bucket?._id ? "is-selected" : ""}
                            onClick={() => selectFilterBucket(bucket)}
                          >
                            {bucket?.name}
                          </button>
                          {bucket?.subBuckets?.map((subBucket) => (
                            <button
                              key={subBucket?._id}
                              type="button"
                              className={`files-filter-popover__subitem ${filterSelectedBucket === subBucket?._id ? "is-selected" : ""}`}
                              onClick={() => selectFilterBucket(subBucket)}
                            >
                              {subBucket?.name}
                            </button>
                          ))}
                        </Fragment>
                      ))}
                    </div>
                    <div className="files-filter-popover__section">
                      <span>Tag</span>
                      <button
                        type="button"
                        className={!filterTagId ? "is-selected" : ""}
                        onClick={() => {
                          setFilterTagId("");
                          setFilterTag("");
                          setShowFilters(false);
                        }}
                      >
                        All tags
                      </button>
                      {tagOptions.map((tag) => (
                        <button
                          key={tag?._id}
                          type="button"
                          className={filterTagId === tag?._id ? "is-selected" : ""}
                          onClick={() => selectFilterTag(tag)}
                        >
                          {tag?.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Menu as="div" className="files-sort-control">
                <Menu.Button className="files-button files-button--outline">
                  <span>{activeSort.label}</span>
                  <ChevronDown size={15} />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="files-sort-menu">
                    {sortOptions.map((option) => (
                      <Menu.Item key={option.value}>
                        {({ active }) => (
                          <button
                            type="button"
                            className={`${active ? "is-active" : ""} ${cardFilterDefault === option.value ? "is-selected" : ""}`}
                            onClick={() => handleFilterFilesText(option)}
                          >
                            {option.label}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>

              <div className="files-view-toggle" aria-label="Choose view">
                <button
                  type="button"
                  className={viewMode === "grid" ? "is-active" : ""}
                  onClick={() => setViewMode("grid")}
                  aria-label="Grid view"
                  aria-pressed={viewMode === "grid"}
                >
                  <Grid3X3 size={17} />
                </button>
                <button
                  type="button"
                  className={viewMode === "list" ? "is-active" : ""}
                  onClick={() => setViewMode("list")}
                  aria-label="List view"
                  aria-pressed={viewMode === "list"}
                >
                  <List size={18} />
                </button>
              </div>
            </div>
          </div>

          {hasFilters && (
            <div className="files-active-filters">
              <span>Showing</span>
              {filterBucket && <button type="button" onClick={() => { setFilterSelectedBucket(""); setFilterBucket(""); }}>Bucket: {filterBucket} <X size={13} /></button>}
              {filterTag && <button type="button" onClick={() => { setFilterTagId(""); setFilterTag(""); }}>Tag: {filterTag} <X size={13} /></button>}
              <button type="button" className="files-active-filters__clear" onClick={clearFilters}>Clear all</button>
            </div>
          )}

          {selectedItems.length > 0 && (
            <div className="files-selection-bar">
              <strong>{selectedItems.length} selected</strong>
              <div>
                <button type="button" onClick={handleSelectedDownload}><Download size={16} /> <span>Download</span></button>
                <button type="button" onClick={openMoveForSelected}><FolderInput size={16} /> <span>Move to</span></button>
                <button type="button" className="files-selection-bar__danger" onClick={() => setIsDeleteOpen(true)}><Trash2 size={16} /> <span>Delete</span></button>
              </div>
              <button type="button" className="files-icon-button" onClick={() => setSelectedItems([])} aria-label="Clear selection"><X size={17} /></button>
            </div>
          )}

          <main className="files-content">
            {visibleFiles.length === 0 ? (
              <div className="files-empty-state">
                <FileText size={42} />
                <h2>No files found</h2>
                <p>{searchQuery || hasFilters ? "Try another search or filter." : "Upload your first file to get started."}</p>
                {!searchQuery && !hasFilters && (
                  <button type="button" className="files-button files-button--primary" onClick={() => setFilePopup(true)}>
                    <Upload size={16} /> Upload file
                  </button>
                )}
              </div>
            ) : viewMode === "list" ? (
              <div className="files-list-card">
                <div className="files-list-card__header files-list-row">
                  <label className="files-checkbox">
                    <input type="checkbox" checked={areAllVisibleFilesSelected} onChange={toggleVisibleSelection} aria-label="Select all visible files" />
                    <span>{areAllVisibleFilesSelected && <Check size={12} strokeWidth={3} />}</span>
                  </label>
                  <span>Name</span>
                  <span>Bucket</span>
                  <span>Size</span>
                  <span>Modified</span>
                  <span>Shared with</span>
                  <span>Tags</span>
                  <span aria-hidden="true" />
                </div>

                {visibleFiles.map((file) => (
                  <article
                    key={file?._id}
                    className={`files-list-row files-list-row--item ${selectedItems.includes(file?._id) ? "is-selected" : ""}`}
                    onClick={() => navigate(`/app/card-detail/${file?._id}?card=file`)}
                  >
                    {renderSelectionControl(file)}
                    <div className="files-name-cell">
                      <FilePreview file={file} size="small" />
                      <div>
                        <strong title={file?.title}>{file?.title || "Untitled file"}</strong>
                      </div>
                    </div>
                    <span className="files-list-row__muted">{file?.bucketId?.name || "No bucket"}</span>
                    <span className="files-list-row__muted">{formatFileSize(file?.sizeInBytes)}</span>
                    <span className="files-list-row__muted">{formatDate(file?.createdAt)}</span>
                    <span className="files-list-row__muted files-list-row__shared" title={getSharedLabel(file)}>{getSharedLabel(file)}</span>
                    <span className={`files-tag ${getTagLabel(file) === "No tags" ? "files-tag--empty" : ""}`}>{getTagLabel(file)}</span>
                    <div className="files-list-row__actions">
                      {getNotePreview(file) && <span className="files-note-indicator" title={getNotePreview(file)}><StickyNote size={16} /></span>}
                      <button
                        type="button"
                        className={`files-star ${file?.starred ? "files-star--active" : ""}`}
                        onClick={(event) => { event.stopPropagation(); handleStarClick(file); }}
                        aria-label={file?.starred ? "Remove star" : "Star file"}
                      >
                        <Star size={17} fill={file?.starred ? "currentColor" : "none"} />
                      </button>
                      {renderFileActions(file)}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="files-grid">
                {visibleFiles.map((file) => {
                  const selected = selectedItems.includes(file?._id);
                  return (
                    <article
                      key={file?._id}
                      className={`files-grid-card ${selected ? "is-selected" : ""}`}
                      onClick={() => navigate(`/app/card-detail/${file?._id}?card=file`)}
                    >
                      <div className="files-grid-card__tools">
                        {renderSelectionControl(file)}
                        <button
                          type="button"
                          className={`files-star ${file?.starred ? "files-star--active" : ""}`}
                          onClick={(event) => { event.stopPropagation(); handleStarClick(file); }}
                          aria-label={file?.starred ? "Remove star" : "Star file"}
                        >
                          <Star size={17} fill={file?.starred ? "currentColor" : "none"} />
                        </button>
                      </div>
                      <div className="files-grid-card__preview">
                        <FilePreview file={file} size="large" />
                      </div>
                      <div className="files-grid-card__body">
                        <strong title={file?.title}>{file?.title || "Untitled file"}</strong>
                        <span>{formatFileSize(file?.sizeInBytes)} · {formatDate(file?.createdAt)}</span>
                        <div>
                          <span className={`files-tag ${getTagLabel(file) === "No tags" ? "files-tag--empty" : ""}`}>{getTagLabel(file)}</span>
                          {getNotePreview(file) && <span className="files-note-indicator" title={getNotePreview(file)}><StickyNote size={16} /></span>}
                          {renderFileActions(file)}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </main>

          {filePopup && (
            <div className="files-modal-backdrop" role="presentation" onMouseDown={closeFilePopup}>
              <section className="files-upload-modal" role="dialog" aria-modal="true" aria-labelledby="files-upload-title" onMouseDown={(event) => event.stopPropagation()}>
                <header>
                  <div>
                    <h2 id="files-upload-title">Upload files</h2>
                    <p>Add up to three files to a bucket.</p>
                  </div>
                  <button type="button" className="files-icon-button" onClick={closeFilePopup} aria-label="Close upload dialog"><X size={20} /></button>
                </header>

                <div className="files-upload-modal__fields">
                  <div className="files-form-field files-form-field--with-menu">
                    <label htmlFor="upload-bucket">Bucket</label>
                    <div className="files-form-select">
                      <input
                        id="upload-bucket"
                        value={searchBucket}
                        onChange={(event) => { setSearchBucket(event.target.value); setIsBucketOpen(true); }}
                        onFocus={() => setIsBucketOpen(true)}
                        placeholder="Select a bucket"
                        autoComplete="off"
                      />
                      <ChevronDown size={16} />
                    </div>
                    {isBucketOpen && (
                      <div className="files-form-menu">
                        {filteredBuckets.length ? filteredBuckets.map((bucket) => (
                          <Fragment key={bucket?._id}>
                            <button type="button" onClick={() => selectUploadBucket(bucket)}>{bucket?.name}</button>
                            {bucket?.subBuckets?.map((subBucket) => (
                              <button type="button" className="files-form-menu__subitem" key={subBucket?._id} onClick={() => selectUploadSubBucket(subBucket)}>{subBucket?.name}</button>
                            ))}
                          </Fragment>
                        )) : <span>No buckets found</span>}
                      </div>
                    )}
                  </div>

                  <div className="files-form-field files-form-field--with-menu">
                    <label>Tags</label>
                    <div className="files-tags-input" onClick={() => setIsTagOpen(true)}>
                      <TagsInput value={tags} onChange={setTags} inputValue={inputValueSet} onChangeInput={(value) => { setInputValueSet(value); setIsTagOpen(true); }} />
                      <ChevronDown size={16} />
                    </div>
                    {isTagOpen && (
                      <div className="files-form-menu">
                        {filteredTags.length ? filteredTags.map((tag) => (
                          <button type="button" key={tag?._id} onClick={() => selectUploadTag(tag)}>{tag?.name}</button>
                        )) : <span>No matching tags found</span>}
                      </div>
                    )}
                  </div>

                  <div className="files-form-field">
                    <label>Notes</label>
                    <ReactQuill theme="snow" value={note} onChange={setNotes} placeholder="Add notes (optional)" />
                  </div>
                </div>

                <div className="files-dropzone" onDrop={handleDrop} onDragOver={(event) => event.preventDefault()}>
                  <Upload size={24} />
                  <p>Drag and drop files here, or <button type="button" onClick={() => { fileInputRef.current.value = null; fileInputRef.current.click(); }}>browse</button></p>
                  <span>Up to 3 files · 10 MB each or 30 MB total</span>
                  <input ref={fileInputRef} type="file" multiple onChange={handleFileChange} onClick={(event) => { event.target.value = null; }} />
                </div>

                {files.length > 0 && (
                  <ul className="files-upload-list">
                    {files.map((file, index) => (
                      <li key={`${file.name}-${index}`}>
                        <FileTypeIcon name={file.name} size="small" />
                        <span title={file.name}>{file.name}</span>
                        <em>{formatFileSize(file.size)}</em>
                        <button type="button" className="files-icon-button" onClick={() => handleRemoveFile(index)} aria-label={`Remove ${file.name}`}><X size={16} /></button>
                      </li>
                    ))}
                  </ul>
                )}

                <footer>
                  <button type="button" className="files-button files-button--text" onClick={closeFilePopup}>Cancel</button>
                  <button type="button" className="files-button files-button--primary" onClick={handleSubmit}><Upload size={16} /> Upload</button>
                </footer>
              </section>
            </div>
          )}

          {fileSharePopup && (
            <ShareCard
              shareBucketId={shareFileId}
              closeSharePop={() => setFileSharePopup(false)}
              cardsRefresh={refreshFiles}
            />
          )}

          <Transition appear show={isDeleteOpen} as={Fragment}>
            <Dialog as="div" className="files-delete-dialog" onClose={() => setIsDeleteOpen(false)}>
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                <div className="files-modal-backdrop" />
              </Transition.Child>
              <div className="files-delete-dialog__position">
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="files-delete-dialog__panel">
                    <Trash2 size={24} />
                    <Dialog.Title>Delete {selectedItems.length === 1 ? "file" : "files"}?</Dialog.Title>
                    <Dialog.Description>This action cannot be undone.</Dialog.Description>
                    <div>
                      <button type="button" className="files-button files-button--outline" onClick={() => setIsDeleteOpen(false)}>Cancel</button>
                      <button type="button" className="files-button files-button--danger" onClick={handleDeleteCard}>Delete</button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition>

          <Transition appear show={isEditOpen} as={Fragment}>
            <Dialog as="div" className="files-delete-dialog" onClose={() => setIsEditOpen(false)}>
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                <div className="files-modal-backdrop" />
              </Transition.Child>
              <div className="files-delete-dialog__position">
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="files-dialog-panel files-dialog-panel--form">
                    <Dialog.Title>Edit file name</Dialog.Title>
                    <input value={editingFileName} onChange={(event) => setEditingFileName(event.target.value)} autoFocus aria-label="File name" />
                    <div>
                      <button type="button" className="files-button files-button--outline" onClick={() => setIsEditOpen(false)}>Cancel</button>
                      <button type="button" className="files-button files-button--primary" onClick={handleRenameFile}>Save</button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition>

          <Transition appear show={isCopyOpen} as={Fragment}>
            <Dialog as="div" className="files-delete-dialog" onClose={() => setIsCopyOpen(false)}>
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                <div className="files-modal-backdrop" />
              </Transition.Child>
              <div className="files-delete-dialog__position">
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="files-dialog-panel files-dialog-panel--form">
                    <Dialog.Title>Copy file to</Dialog.Title>
                    <p>Choose the bucket that should receive a copy.</p>
                    <div className="files-copy-bucket-picker">
                      <input value={copyBucketSearch} onChange={(event) => { setCopyBucketSearch(event.target.value); setCopyBucketId(""); }} placeholder="Search buckets" aria-label="Destination bucket" />
                      <div>
                        {copyBuckets.map((bucket) => (
                          <Fragment key={bucket?._id}>
                            <button type="button" className={copyBucketId === bucket?._id ? "is-selected" : ""} onClick={() => selectCopyBucket(bucket)}>{bucket?.name}</button>
                            {bucket?.subBuckets?.map((subBucket) => (
                              <button type="button" className={`files-copy-bucket-picker__subitem ${copyBucketId === subBucket?._id ? "is-selected" : ""}`} key={subBucket?._id} onClick={() => selectCopyBucket(subBucket)}>{subBucket?.name}</button>
                            ))}
                          </Fragment>
                        ))}
                        {!copyBuckets.length && <span>No buckets found</span>}
                      </div>
                    </div>
                    <div>
                      <button type="button" className="files-button files-button--outline" onClick={() => setIsCopyOpen(false)}>Cancel</button>
                      <button type="button" className="files-button files-button--primary" onClick={handleCopyFile}>Copy</button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition>

          {moveModal && (
            <MoveModal
              openMove={openMove}
              closeModal={closeMove}
              selectedItems={selectedItems}
              cardUpdate={refreshFiles}
              buckets={buckets}
            />
          )}
        </section>
      )}
    </>
  );
}

export default Files;
